/**
 * Deals list query: hide archived by default, page 5/10/25 (Q20),
 * optional salesperson + office (lot) filters.
 */
import {
  formatHostOffice,
  formatHostSalespersonName,
  hostOfficeKey,
  hostSalespersonKey,
  type HostSalesperson,
} from "./hostSalesperson.js";

export const DEALS_PAGE_SIZES = [5, 10, 25] as const;
export type DealsPageSize = (typeof DEALS_PAGE_SIZES)[number];
export const DEFAULT_DEALS_PAGE_SIZE: DealsPageSize = 10;
/** Cap for pickers (payables, lookup, service original deal). */
export const DEALS_PICKER_PAGE_SIZE = 200;
export const DEALS_LIST_FETCH_CAP = 500;

export const DEALS_LIST_STATUSES = ["active", "open", "closed", "archived", "all"] as const;
export type DealsListStatus = (typeof DEALS_LIST_STATUSES)[number];
export const DEFAULT_DEALS_LIST_STATUS: DealsListStatus = "active";

export const DEALS_TYPE_MATCH_FILTERS = ["", "matched", "product_only", "unresolved"] as const;
export type DealsTypeMatchFilter = (typeof DEALS_TYPE_MATCH_FILTERS)[number];

export type DealsListQuery = {
  status: DealsListStatus;
  salesperson: string;
  office: string;
  type_match: DealsTypeMatchFilter;
  page: number;
  page_size: number;
};

export type DealsListFacet = { key: string; label: string };
export type DealsListFacets = {
  salespeople: DealsListFacet[];
  offices: DealsListFacet[];
};

export type DealsListFilterable = {
  status: string;
  salesperson_key: string | null;
  office_key: string | null;
  type_match_status: string | null;
};

function firstQueryValue(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  if (Array.isArray(raw) && typeof raw[0] === "string") return raw[0].trim();
  return "";
}

export function parseDealsListStatus(raw: unknown): DealsListStatus {
  const v = firstQueryValue(raw).toLowerCase();
  if (!v) return DEFAULT_DEALS_LIST_STATUS;
  return (DEALS_LIST_STATUSES as readonly string[]).includes(v) ? (v as DealsListStatus) : DEFAULT_DEALS_LIST_STATUS;
}

export function parseDealsPageSize(raw: unknown, fallback: number = DEFAULT_DEALS_PAGE_SIZE): number {
  const n = Number(firstQueryValue(raw));
  if (!Number.isInteger(n) || n < 1) return fallback;
  return Math.min(n, DEALS_PICKER_PAGE_SIZE);
}

export function parseDealsPage(raw: unknown): number {
  const n = Number(firstQueryValue(raw));
  if (!Number.isInteger(n) || n < 1) return 1;
  return n;
}

export function parseDealsTypeMatchFilter(raw: unknown): DealsTypeMatchFilter {
  const v = firstQueryValue(raw).toLowerCase();
  return (DEALS_TYPE_MATCH_FILTERS as readonly string[]).includes(v) ? (v as DealsTypeMatchFilter) : "";
}

export function parseDealsListQuery(raw: Record<string, unknown>): DealsListQuery {
  return {
    status: parseDealsListStatus(raw.status),
    salesperson: firstQueryValue(raw.salesperson),
    office: firstQueryValue(raw.office),
    type_match: parseDealsTypeMatchFilter(raw.type_match),
    page: parseDealsPage(raw.page),
    page_size: parseDealsPageSize(raw.page_size),
  };
}

export function dealStatusMatches(status: string, filter: DealsListStatus): boolean {
  if (filter === "all") return true;
  if (filter === "active") return status !== "archived";
  return status === filter;
}

export function dealMatchesListFilters(row: DealsListFilterable, q: DealsListQuery): boolean {
  if (!dealStatusMatches(row.status, q.status)) return false;
  if (q.salesperson && row.salesperson_key !== q.salesperson) return false;
  if (q.office && row.office_key !== q.office) return false;
  if (q.type_match && (row.type_match_status || "unresolved") !== q.type_match) return false;
  return true;
}

export function dealsListFieldsFromHost(host: HostSalesperson | null): {
  salesperson_name: string | null;
  salesperson_key: string | null;
  office_name: string | null;
  office_key: string | null;
} {
  if (!host) {
    return { salesperson_name: null, salesperson_key: null, office_name: null, office_key: null };
  }
  const salesperson_key = hostSalespersonKey(host) || null;
  const office_key = hostOfficeKey(host) || null;
  const salesperson_name = formatHostSalespersonName(host) || null;
  const office_name = formatHostOffice(host) || null;
  return { salesperson_name, salesperson_key, office_name, office_key };
}

function uniqueFacets(rows: Array<{ key: string | null; label: string | null }>): DealsListFacet[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const key = (row.key ?? "").trim();
    if (!key || seen.has(key)) continue;
    seen.set(key, (row.label ?? "").trim() || key);
  }
  return [...seen.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildDealsListFacets(
  rows: Array<{
    salesperson_key: string | null;
    salesperson_name: string | null;
    office_key: string | null;
    office_name: string | null;
  }>,
  q: Pick<DealsListQuery, "salesperson" | "office">
): DealsListFacets {
  const forSalespeople = q.office ? rows.filter((r) => r.office_key === q.office) : rows;
  const forOffices = q.salesperson ? rows.filter((r) => r.salesperson_key === q.salesperson) : rows;
  return {
    salespeople: uniqueFacets(forSalespeople.map((r) => ({ key: r.salesperson_key, label: r.salesperson_name }))),
    offices: uniqueFacets(forOffices.map((r) => ({ key: r.office_key, label: r.office_name }))),
  };
}

/** When the deal landed in the app (ops_deal_entities.created_at). */
export function formatDealReceivedAt(iso: string | null | undefined): string {
  const raw = typeof iso === "string" ? iso.trim() : "";
  if (!raw) return "";
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function paginateDealsList<T>(rows: T[], page: number, pageSize: number): {
  deals: T[];
  total: number;
  page: number;
  page_size: number;
} {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  return {
    deals: rows.slice(start, start + pageSize),
    total,
    page: safePage,
    page_size: pageSize,
  };
}
